import React, { useEffect, useRef } from "react";
import { Heading } from "@components/Heading/Heading";
import { Container } from "@components/Container/Container";
import { ImageField } from "@helpers/interfaces/image.js";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { Module } from "@components/Module/Module";

interface CDRibbonTrackProps {
  Title: string;
  TrackUrl: string;
  Key: string;
}
interface CDRibbonProps {
  Image: ImageField;
  Headline: string;
  Description: string;
  ImageLink?: string;
  Tracks: CDRibbonTrackProps[];
}

async function initializeCDRibbon(ref: HTMLElement) {
  await import("./plyr/plyr.js");
  const { default: CDRibbonInit } = await import("./CDRibbon.init.js");
  new CDRibbonInit(ref);
}

const CDRibbon: React.FC<CDRibbonProps> = ({
  Headline,
  Description,
  Image,
  Tracks
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      initializeCDRibbon(containerRef.current);
    }
  }, []);

  return (
    <div ref={containerRef} className="cd-ribbon">
      <Module animate>
        <Container>
          <Heading as="h2" className="headline">
            {Headline}
          </Heading>
          <div className="cd-ribbon__content">
            {Image && <UmbracoImage image={Image} />}
            {Description && (
              <div
                className="cd-ribbon__content__text"
                dangerouslySetInnerHTML={{ __html: Description }}
              ></div>
            )}
          </div>
          <section className="cd-ribbon__player">
            <ul className="cd-ribbon__playlist cd-ribbon__playlist--22">
              <li className="cd-ribbon__audio-player">
                <Heading as="h3" className="cd-ribbon__active-title">
                  <span className="cd-ribbon__active-title-icon">
                    <svg
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 18.3 11.1"
                    >
                      <path d="M7.7,10.6L0.6,3.4c-0.8-0.8-0.8-2,0-2.8l0,0c0.8-0.8,2-0.8,2.8,0l7.1,7.1c0.8,0.8,0.8,2,0,2.8l0,0 C9.8,11.3,8.5,11.3,7.7,10.6z"></path>
                      <path d="M7.7,7.7l7.1-7.1c0.8-0.8,2-0.8,2.8,0l0,0c0.8,0.8,0.8,2,0,2.8l-7.1,7.1c-0.8,0.8-2,0.8-2.8,0l0,0C6.9,9.8,6.9,8.5,7.7,7.7z"></path>
                    </svg>
                  </span>
                  <span className="cd-ribbon__active-title-text"></span>
                </Heading>
                <audio className="cd-ribbon__audio" preload="">
                  Din browser understøtter ikke afspilning af lyd. Prøv venligst
                  med en nyere browser!
                </audio>
              </li>
              {Tracks &&
                Tracks.map((track: CDRibbonTrackProps) => {
                  return (
                    <li key={track.Key} className="cd-ribbon__audio-item">
                      <a
                        href={track.TrackUrl}
                        className="cd-ribbon__audio-link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span className="cd-ribbon__audio-link-icon">
                          <svg viewBox="0 0 18 18" width="100%" height="100%">
                            <path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
                          </svg>
                        </span>
                        {track.Title ?? "(ingen titel)"}
                      </a>
                    </li>
                  );
                })}
            </ul>
          </section>
        </Container>
      </Module>
    </div>
  );
};

export { CDRibbon };
